// src/pages/RegistryPage.tsx
//
// The MCP Registry page (route /mcp-registry). It is the canonical surface that
// renders the full server catalog (Requirement 10.1).
//
// On mount it fetches the catalog exactly once via api.registry.getMcpCatalog(),
// wrapped in a 10-second AbortController timeout (Requirements 2.1, 2.2, 2.4),
// then normalizes the response with normalizeCatalog. It renders one of four
// states:
//
//   loading  — request in flight within 10s (Requirement 2.2)
//   error    — fetch failure OR 10s timeout, with a Retry control that re-runs
//              the fetch WITHOUT a page reload (Requirements 2.4, 11.3, 11.4)
//   empty    — catalog normalizes to zero entries (Requirement 2.5)
//   loaded   — entries grouped by Category, one CategoryGroup per group
//              (Requirements 1.2, 2.3)
//
// The remote-http base URL is never hardcoded — it is resolved via
// resolveMcpBaseUrl() (Requirement 10.5).

import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, Loader2, PlugZap, Boxes } from 'lucide-react'
import { api } from '../api/client'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { CategoryGroup } from '../components/registry/CategoryGroup'
import { groupByCategory, normalizeCatalog, type CatalogEntry } from '../lib/catalog'
import { resolveMcpBaseUrl } from '../lib/mcpConfig'

/** Maximum time (ms) to wait for the catalog request before treating it as an error. */
const CATALOG_TIMEOUT_MS = 10_000

type LoadState =
  | { status: 'loading' }
  // `detail` carries the actual failure (HTTP status + body, or a timeout note)
  // so the user sees the real error and can report/fix it — never a guessed cause.
  | { status: 'error'; detail: string }
  | { status: 'empty' }
  | { status: 'loaded'; entries: CatalogEntry[] }

export function RegistryPage() {
  const [state, setState] = useState<LoadState>({ status: 'loading' })
  // `attempt` drives re-fetches on Retry without reloading the page.
  const [attempt, setAttempt] = useState(0)
  const baseUrl = resolveMcpBaseUrl()

  // Fetch the catalog once, with a 10s AbortController timeout. Returns a
  // cleanup function that aborts the in-flight request and clears the timer.
  const load = useCallback((onResult: (next: LoadState) => void) => {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), CATALOG_TIMEOUT_MS)
    let settled = false

    api.registry
      .getMcpCatalog(controller.signal)
      .then((response) => {
        if (settled) return
        const entries = normalizeCatalog(response)
        onResult(entries.length === 0 ? { status: 'empty' } : { status: 'loaded', entries })
      })
      .catch((err: unknown) => {
        if (settled) return
        // Surface the REAL failure verbatim so it can be reported and fixed —
        // never a guessed cause. The api client throws `Error("<status> <text>:
        // <body>")` for non-2xx; an aborted request is the 10s timeout.
        const detail = controller.signal.aborted
          ? `Request timed out after ${CATALOG_TIMEOUT_MS / 1000}s (GET /api/v1/registry/mcp-catalog).`
          : err instanceof Error
            ? err.message
            : String(err)
        onResult({ status: 'error', detail })
      })
      .finally(() => {
        clearTimeout(timeout)
      })

    return () => {
      settled = true
      clearTimeout(timeout)
      controller.abort()
    }
  }, [])

  useEffect(() => {
    setState({ status: 'loading' })
    let active = true
    const cancel = load((next) => {
      if (active) setState(next)
    })
    return () => {
      active = false
      cancel()
    }
    // `attempt` drives re-fetches on Retry without reloading the page.
  }, [load, attempt])

  // Retry re-runs the fetch in place (no page reload) by bumping `attempt`,
  // which re-triggers the effect above (Requirements 11.3, 11.4).
  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  return (
    <div style={{ padding: 'var(--space-4)', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 'var(--space-2)' }}>
        <PlugZap size={22} color="var(--brand)" />
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>MCP Registry</h1>
      </div>
      <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 'var(--space-4)' }}>
        Browse the available MCP servers and install any of them into your IDE.
      </p>

      {state.status === 'loading' && <LoadingState />}
      {state.status === 'error' && <ErrorState detail={state.detail} onRetry={retry} />}
      {state.status === 'empty' && <EmptyState />}
      {state.status === 'loaded' &&
        groupByCategory(state.entries.filter(e => e.serverKind === 'remote-http')).map((group) => (
          <CategoryGroup key={group.category} category={group.category} entries={group.entries} baseUrl={baseUrl} />
        ))}

      {state.status === 'loaded' && <DevToolsSection entries={state.entries.filter(e => e.serverKind === 'local-command')} baseUrl={baseUrl} />}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

/** Loading indication shown while the catalog request is in flight (within 10s). */
function LoadingState() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-5)',
        color: 'var(--text-muted)',
      }}
    >
      <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--brand)' }} />
      <div style={{ fontSize: 14 }}>Loading MCP servers…</div>
    </div>
  )
}

/** Error indication shown on fetch failure or timeout, with a Retry control.
 *
 * Renders the ACTUAL error detail (HTTP status + body, or the timeout note)
 * verbatim and selectable, so the developer can copy and report it. We do not
 * substitute a guessed cause (e.g. "check your connection").
 */
function ErrorState({ detail, onRetry }: Readonly<{ detail: string; onRetry: () => void }>) {
  return (
    <Card>
      <div style={{ textAlign: 'center', padding: 'var(--space-5)', color: 'var(--text-muted)' }}>
        <AlertTriangle size={40} color="var(--error)" style={{ margin: '0 auto 12px', opacity: 0.8 }} />
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>
          Couldn&apos;t load the MCP registry
        </div>
        <div style={{ fontSize: 14, marginBottom: 'var(--space-2)' }}>
          The request to <code>GET /api/v1/registry/mcp-catalog</code> failed. The exact error is shown
          below — please report it if it persists.
        </div>
        <pre
          style={{
            textAlign: 'left',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'var(--font-mono, monospace)',
            fontSize: 12,
            color: 'var(--error)',
            background: 'var(--surface-hover)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-s)',
            padding: 'var(--space-2)',
            margin: '0 auto var(--space-3)',
            maxWidth: 720,
            userSelect: 'text',
          }}
        >
          {detail}
        </pre>
        <Button variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </Card>
  )
}

/** Empty-state indication shown when the catalog returns zero entries. */
function EmptyState() {
  return (
    <Card>
      <div style={{ textAlign: 'center', padding: 'var(--space-5)', color: 'var(--text-muted)' }}>
        <Boxes size={40} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>
          No MCP servers available
        </div>
        <div style={{ fontSize: 14 }}>There are no MCP servers currently available in the registry.</div>
      </div>
    </Card>
  )
}

// ─── Developer Tools Section ─────────────────────────────────────────────────

function DevToolsSection({ entries, baseUrl }: Readonly<{ entries: CatalogEntry[]; baseUrl: string }>) {
  if (entries.length === 0) return null
  return (
    <CategoryGroup category="Developer Tools" entries={entries} baseUrl={baseUrl} />
  )
}
